import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import RenderRealitySlider from "@/components/RenderRealitySlider";
import SaveProjectButton from "@/components/SaveProjectButton";
import { fetchProject, fetchProjects } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: project, isLoading } = useQuery({
    queryKey: ["project", slug],
    queryFn: () => fetchProject(slug!),
    enabled: !!slug,
  });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });

  usePageMeta(
    project ? `${project.title}, ${project.city} — Render vs. Reality | Shriya Redkar Interiors` : "Project | Shriya Redkar Interiors",
    project?.summary
  );

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian">
        <p className="font-mono text-sm text-zinc-500">Loading project…</p>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-obsidian px-6 text-center">
        <p className="font-serif text-4xl text-zinc-100">This project isn't in the portfolio.</p>
        <Link to="/portfolio" className="btn-gold" data-testid="project-notfound-back-link">
          Back to portfolio
        </Link>
      </main>
    );
  }

  const next = projects?.find((p) => p.sort > project.sort) ?? projects?.[0];
  const inExecution = project.stage === "in_execution" || !project.reality_url;

  return (
    <main className="bg-obsidian">
      {/* HERO */}
      <section className="relative flex min-h-[72vh] items-end overflow-hidden">
        <img
          src={inExecution ? project.render_url : project.reality_url}
          alt={`${project.title} — ${inExecution ? "approved 3D render" : "finished home"}`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/60" />
        <div className="container-x relative z-10 pb-14 pt-40">
          <Reveal>
            <Link
              to="/portfolio"
              data-testid="project-back-to-portfolio"
              className="inline-flex items-center gap-2 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4" /> All projects
            </Link>
            <p className="eyebrow mt-8" data-testid="project-meta-top">
              {project.city} · {project.property_type} · {project.year}
            </p>
            <h1 className="mt-4 font-serif text-4xl font-light text-zinc-50 sm:text-6xl lg:text-7xl" data-testid="project-title">
              {project.title}
            </h1>
            <div className="mt-8">
              <SaveProjectButton slug={project.slug} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SPECS BAR */}
      <section className="border-y border-white/10 bg-obsidian-soft">
        <div className="container-x grid grid-cols-2 gap-6 py-8 sm:grid-cols-4" data-testid="project-specs">
          {[
            { k: "City", v: project.city },
            { k: "On site", v: `${project.timeline_days} days` },
            { k: "Scope", v: project.budget_label },
            { k: "Area", v: project.area },
          ].map((s) => (
            <div key={s.k}>
              <p className="font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.k}</p>
              <p className="mt-2 font-serif text-xl text-zinc-100">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE PROOF */}
      {inExecution ? (
        <section className="container-x py-20 lg:py-28" data-testid="project-renders">
          <Reveal>
            <p className="eyebrow">Approved 3D · site photos to follow</p>
            <h2 className="mt-5 max-w-3xl font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-5xl">
              The views the client signed off. <span className="italic text-gold">Reality arrives at handover.</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm text-zinc-500">
              Every render below will be published beside its finished-site photograph — including anything that moved.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {project.pairs.map((pair, i) => (
              <Reveal key={pair.room} delay={0.05 * i} className={i === 0 ? "lg:col-span-2" : ""}>
                <div className="frame-clipped relative overflow-hidden bg-obsidian-card">
                  <img
                    src={pair.render_url}
                    alt={`${project.title} — ${pair.room} approved 3D render`}
                    loading={i === 0 ? "eager" : "lazy"}
                    className={`w-full object-cover ${i === 0 ? "aspect-[16/8]" : "aspect-[16/10]"}`}
                    data-testid={`project-render-${i}`}
                  />
                  <span className="absolute left-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-300 backdrop-blur-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> 3D · Approved
                  </span>
                </div>
                <p className="mt-4 flex items-baseline gap-4">
                  <span className="font-mono text-xs text-zinc-600">0{i + 1}</span>
                  <span className="font-serif text-2xl text-zinc-100">{pair.room}</span>
                </p>
                {pair.note && <p className="mt-2 text-sm leading-relaxed text-zinc-500">{pair.note}</p>}
              </Reveal>
            ))}
          </div>
        </section>
      ) : (
      <section className="container-x py-20 lg:py-28">
        <Reveal>
          <p className="eyebrow">The proof</p>
          <h2 className="mt-5 max-w-3xl font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-5xl">
            The render you approved. <span className="italic text-gold">The home you'd walk into.</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm text-zinc-500">Drag the handle to compare — left is the 3D, right is reality.</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12">
          <RenderRealitySlider
            idPrefix="project-proof"
            renderSrc={project.render_url}
            realitySrc={project.reality_url}
            renderAlt={`${project.title} — approved 3D render`}
            realityAlt={`${project.title} — finished reality photo`}
            materials={project.materials}
            duration={`${project.timeline_days} days`}
            city={project.city}
            budget={project.budget_label}
            note={project.honesty}
          />
        </Reveal>
      </section>
      )}

      {/* ROOM BY ROOM */}
      {!inExecution && project.pairs.length > 1 && (
        <section className="container-x border-t border-white/10 py-20 lg:py-28" data-testid="project-rooms">
          <Reveal>
            <p className="eyebrow">Room by room</p>
            <h2 className="mt-5 max-w-3xl font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-5xl">
              Every room, <span className="italic text-gold">checked against its render.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-14 lg:grid-cols-2 lg:gap-x-10">
            {project.pairs.slice(1).map((pair, i) => (
              <Reveal key={pair.room} delay={0.05 * i} className="flex flex-col">
                <p className="mb-4 flex items-baseline gap-4">
                  <span className="font-mono text-xs text-zinc-600">0{i + 2}</span>
                  <span className="font-serif text-2xl text-zinc-100" data-testid={`project-room-title-${i}`}>{pair.room}</span>
                </p>
                <RenderRealitySlider
                  idPrefix={`project-room-${i}`}
                  renderSrc={pair.render_url}
                  realitySrc={pair.reality_url}
                  renderAlt={`${project.title} — ${pair.room} approved 3D render`}
                  realityAlt={`${project.title} — ${pair.room} finished site photo`}
                />
                {pair.note && <p className="mt-4 text-sm leading-relaxed text-zinc-500">{pair.note}</p>}
              </Reveal>
            ))}
          </div>
        </section>
      )}
      <section className="border-t border-white/10 bg-obsidian-soft">
        <div className="container-x grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">The brief</p>
            <p className="mt-6 font-serif text-2xl font-light leading-relaxed text-zinc-200 sm:text-3xl">
              {project.summary}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <p className="eyebrow">Honest caption</p>
            <div className="mt-6 border-l-2 border-gold/60 pl-6">
              <p className="text-base italic leading-relaxed text-zinc-300">{project.honesty}</p>
            </div>
            <p className="mt-8 font-display text-[10px] uppercase tracking-[0.2em] text-zinc-500">Materials</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {project.materials.map((m) => (
                <span key={m} className="border border-white/15 px-3 py-1.5 text-xs text-zinc-300">
                  {m}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA + NEXT */}
      <section className="container-x grid gap-6 py-20 lg:grid-cols-2 lg:py-24">
        <Reveal>
          <div className="flex h-full flex-col justify-between border border-white/10 bg-obsidian-card p-10">
            <div>
              <p className="eyebrow">Your project</p>
              <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-4xl">
                Start the same way {project.title.split(" ")[0]} did — with an honest conversation.
              </h2>
            </div>
            <Link
              to={`/enquire?city=${encodeURIComponent(project.city)}`}
              data-testid="project-cta-enquire"
              className="btn-gold mt-10 self-start"
            >
              Enquire about your home
            </Link>
          </div>
        </Reveal>
        {next && (
          <Reveal delay={0.1}>
            <Link
              to={`/portfolio/${next.slug}`}
              data-testid="project-next-link"
              className="group relative flex h-full min-h-[280px] items-end overflow-hidden border border-white/10"
            >
              <img src={next.render_url} alt={`${next.title} render`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
              <div className="relative z-10 flex w-full items-end justify-between p-8">
                <div>
                  <p className="eyebrow">Next project</p>
                  <p className="mt-3 font-serif text-3xl text-zinc-50">{next.title}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-400">{next.city} · {next.budget_label}</p>
                </div>
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-zinc-100 transition-all group-hover:border-gold group-hover:bg-gold group-hover:text-obsidian">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}
      </section>
    </main>
  );
}
